import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, BookOpen, TrendingUp } from "lucide-react";

const GRADES = ["elementary", "middle", "high"];

export default function ParentHub() {
  const { user, updateProfile } = useAuth();
  const [grade, setGrade] = useState(user?.grade_level || "elementary");
  const [tips, setTips] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get(`/parent/tips?grade_level=${grade}`).then(r => setTips(r.data.tips || []));
  }, [grade]);

  useEffect(() => {
    api.get("/progress").then(r => setProgress(r.data)).catch(()=>{});
  }, []);

  const setMyGrade = async (g) => {
    setGrade(g);
    if (user?.role === "parent") await updateProfile({ grade_level: g });
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center"><Users size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Parent Hub</h1>
        </div>
        <p className="text-slate-600">Tips, talking points, and a window into your child's progress.</p>
      </div>

      <div className="flex gap-2">
        {GRADES.map(g => (
          <button key={g} data-testid={`parent-grade-${g}`} onClick={()=>setMyGrade(g)}
            className={`rounded-full px-4 py-2 text-sm font-bold border-2 capitalize ${grade===g ? 'bg-indigo-600 border-indigo-700 text-white':'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'}`}>
            {g}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tips.map((t, i) => (
          <div key={i} data-testid={`tip-${i}`} className="tactile-card p-6">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center mb-3"><BookOpen size={18}/></div>
            <h3 className="text-lg font-bold mb-1" style={{fontFamily:'Outfit'}}>{t.title}</h3>
            <p className="text-slate-600">{t.body}</p>
          </div>
        ))}
      </div>

      <div className="tactile-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp size={20} className="text-emerald-600" />
          <h2 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>Progress Snapshot</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total sessions" v={progress?.total_sessions ?? 0} />
          <Stat label="Breakdowns" v={progress?.by_kind?.breakdown ?? 0} />
          <Stat label="Essays" v={progress?.by_kind?.essay_structure ?? 0} />
          <Stat label="Rubric reviews" v={progress?.by_kind?.rubric_feedback ?? 0} />
        </div>
        {progress?.rubric_trend?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Recent rubric averages</div>
            <div className="flex items-end gap-2 h-32">
              {progress.rubric_trend.map((p, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-emerald-400 rounded-t-lg" style={{height: `${(p.avg/4)*100}%`}} />
                  <span className="text-[10px] text-slate-500">{p.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, v }) => (
  <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
    <div className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{v}</div>
    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
  </div>
);
